import { Box, Center, Text } from "@chakra-ui/layout";
import { Button, Tooltip } from "@chakra-ui/react";
import { TaskPriorityButton } from "@components/modules/resource-planning/tasks/components/task-priority-button";
import { StarButton } from "@components/ui-kit/star-button";
import { Trans, t } from "@lingui/macro";
import {
  type SortOrder,
  TaskOrderByColumnEnum,
} from "@src/__generated__/graphql";
import { LinkIconOnHover } from "@src/components/modules/clients/listing/LinkIconOnHover";
import { TaskStatus } from "@src/components/modules/projects/detail/TaskStatus";
import {
  TaskRowItem,
  type TaskRowItemProps,
} from "@src/components/modules/resource-planning/tasks/listing/components/row-item";
import type { TaskModel } from "@src/components/modules/resource-planning/tasks/listing/models";
import {
  TaskAvatars,
  TaskBudgetedTime,
  TaskDeadline,
  TaskPlannedTime,
} from "@src/components/ui-kit/TaskItem/TaskItemComponents";
import { ExternalAccessIcon } from "@src/components/widgets/ExternalAccessIcon";
import type { WorkspaceStore } from "@src/stores/WorkspaceStore/WorkspaceStore";
import { formatDate } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import type { FC } from "react";

export interface TaskRenderColumn extends TaskRenderColumnSimple {
  orderBy?: TaskOrderByColumnEnum;
  sort?: SortOrder;
  isVisible: boolean;
  disableVisibilityControl?: boolean;
  order: number;
  Component: FC<{ task: TaskModel; gridItemProps?: TaskRowItemProps }>;
}

export type TaskRenderColumnSimple = {
  key: string;
  title: string;
  width: number;
};

export const DEFAULT_TITLE_COLUMN_WIDTH = 300;

export function buildTaskColumns(
  isInternalUser: boolean,
  handleCopyId: (taskId: string) => void,
  workspaceStore: WorkspaceStore,
): TaskRenderColumn[] {
  return [
    {
      key: "title",
      title: t`Title`,
      isVisible: true,
      width: DEFAULT_TITLE_COLUMN_WIDTH,
      disableVisibilityControl: true,
      order: 0,
      orderBy: TaskOrderByColumnEnum.TaskName,
      Component: () => null,
    },
    {
      key: "id",
      title: t`ID`,
      isVisible: true,
      width: 65,
      order: 1,
      orderBy: TaskOrderByColumnEnum.Id,
      Component: observer(function TaskIdColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <LinkIconOnHover dataComponentName="task-id">
              <Button
                justifyContent="start"
                minW="fit-content"
                cursor="copy"
                colorScheme="blue"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopyId(task.id);
                }}
                size="sm"
                variant="link"
              >
                {task.id}
              </Button>
            </LinkIconOnHover>
          </TaskRowItem>
        );
      }),
    },
    {
      key: "status",
      title: t`Status`,
      isVisible: true,
      width: 120,
      order: 2,
      orderBy: TaskOrderByColumnEnum.TaskStatus,
      Component: observer(function TaskStatusColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <Box
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <TaskStatus
                maxW="115px"
                isTruncated
                showArrow="never"
                task={{
                  id: task.id,
                  status: task.status,
                  subtasks: task.subtasks,
                }}
              />
            </Box>
          </TaskRowItem>
        );
      }),
    },
    {
      key: "project",
      title: t`Project`,
      isVisible: true,
      width: 250,
      order: 3,
      orderBy: TaskOrderByColumnEnum.ProjectTitle,
      Component: observer(function TaskProjectColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <Tooltip label={task.projectLabel} placement="top">
              <Text textOverflow="ellipsis" noOfLines={1} w="fit-content">
                {task.ourWorkBudgetItem?.project.code ? (
                  <Text>
                    {task.ourWorkBudgetItem?.project.code}&nbsp;
                    {task.projectLabel}
                  </Text>
                ) : (
                  <Trans>Unassigned</Trans>
                )}
              </Text>
            </Tooltip>
          </TaskRowItem>
        );
      }),
    },
    {
      key: "budget-item",
      title: t`Budget Item`,
      isVisible: isInternalUser,
      disableVisibilityControl: !isInternalUser,
      width: 200,
      order: 4,
      orderBy: TaskOrderByColumnEnum.BudgetItemTitle,
      Component: observer(function TaskBudgetItemColumn({
        task,
        gridItemProps,
      }) {
        return (
          <TaskRowItem {...gridItemProps}>
            {isInternalUser && (
              <Tooltip
                label={
                  task.ourWorkBudgetItem
                    ? task.ourWorkBudgetItem.title
                    : t`Unassigned`
                }
                placement="top"
                shouldWrapChildren
              >
                <Text textOverflow="ellipsis" noOfLines={1} w="fit-content">
                  {task.ourWorkBudgetItem?.title ? (
                    <Text>{task.ourWorkBudgetItem.title}</Text>
                  ) : (
                    <Trans>Unassigned</Trans>
                  )}
                </Text>
              </Tooltip>
            )}
          </TaskRowItem>
        );
      }),
    },
    {
      key: "start-date",
      title: t`Start Date`,
      isVisible: isInternalUser,
      orderBy: TaskOrderByColumnEnum.From,
      width: 120,
      order: 5,
      Component: observer(function TaskStartDateColumn({
        task,
        gridItemProps,
      }) {
        return (
          <TaskRowItem {...gridItemProps}>
            {task.from ? (
              <Text>{formatDate(new Date(task.from))}</Text>
            ) : (
              <Box>&nbsp;</Box>
            )}
          </TaskRowItem>
        );
      }),
    },
    {
      key: "end-date",
      title: t`End Date`,
      isVisible: isInternalUser,
      orderBy: TaskOrderByColumnEnum.To,
      width: 120,
      order: 6,
      Component: observer(function TaskEndDateColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            {task.to ? (
              <Text>{formatDate(new Date(task.to))}</Text>
            ) : (
              <Box>&nbsp;</Box>
            )}
          </TaskRowItem>
        );
      }),
    },
    {
      key: "deadline",
      title: t`Deadline`,
      isVisible: true,
      width: 120,
      order: 7,
      orderBy: TaskOrderByColumnEnum.Deadline,
      Component: observer(function TaskDeadlineColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            {task.deadline ? (
              <TaskDeadline deadline={task.deadline} />
            ) : (
              <Box>&nbsp;</Box>
            )}
          </TaskRowItem>
        );
      }),
    },
    {
      key: "created-at",
      title: t`Created At`,
      isVisible: isInternalUser,
      width: 120,
      order: 8,
      orderBy: TaskOrderByColumnEnum.CreatedAt,
      Component: observer(function TaskCreatedAtColumn({
        task,
        gridItemProps,
      }) {
        return (
          <TaskRowItem {...gridItemProps}>
            {task.created_at ? (
              <Text>{formatDate(new Date(task.created_at))}</Text>
            ) : (
              <Box>&nbsp;</Box>
            )}
          </TaskRowItem>
        );
      }),
    },
    {
      key: "tracked",
      title: t`Tracked`,
      isVisible: isInternalUser,
      disableVisibilityControl: !isInternalUser,
      width: 90,
      order: 9,
      Component: observer(function TaskTrackedColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <TaskBudgetedTime
              spentTime={task.stats?.spent_time ?? 0}
              plannedTime={task.stats?.planned_time ?? 0}
              projectId={task.ourWorkBudgetItem?.project.id}
              budgetItemId={task.ourWorkBudgetItem?.id}
              taskId={task.id}
            />
          </TaskRowItem>
        );
      }),
    },
    {
      key: "planned",
      title: t`Planned`,
      isVisible: isInternalUser,
      disableVisibilityControl: !isInternalUser,
      width: 90,
      order: 10,
      Component: observer(function TaskPlannedColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <TaskPlannedTime
              plannedTime={task.stats?.planned_time ?? 0}
              projectId={task.ourWorkBudgetItem?.project.id}
              spentTime={task.stats?.spent_time ?? 0}
              usesBudgetovac={task.ourWorkBudgetItem?.project.uses_budgetovac}
            />
          </TaskRowItem>
        );
      }),
    },
    {
      key: "externalAccess",
      title: t`External Access`,
      isVisible: isInternalUser,
      disableVisibilityControl: !isInternalUser,
      width: 80,
      order: 11,
      Component: ({ task, gridItemProps }) => (
        <TaskRowItem {...gridItemProps}>
          <Center>
            <ExternalAccessIcon
              type="task"
              id={task.id}
              scopes={task.scopes}
              isTemplate={task.is_template}
            />
          </Center>
        </TaskRowItem>
      ),
    },
    {
      key: "is_starred",
      title: t`Starred`,
      isVisible: true,
      width: 80,
      order: 11,
      orderBy: TaskOrderByColumnEnum.IsStarred,
      Component: observer(function TaskStarredColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <Center>
              <StarButton
                isStarred={task.is_starred}
                isLoading={task.isStarUpdating(task.id)}
                onClick={() => task.toggleStar()}
                label={(starred) => (starred ? t`Unstar task` : t`Star task`)}
              />
            </Center>
          </TaskRowItem>
        );
      }),
    },
    {
      key: "priority",
      title: t`Priority`,
      isVisible: true,
      width: 125,
      order: 12,
      orderBy: TaskOrderByColumnEnum.TaskPriority,
      Component: observer(function TaskPriorityColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <TaskPriorityButton
              priority={task.priority}
              onChange={task.updatePriority}
              taskPriorities={workspaceStore.taskPriorities}
              isLoading={task.isPriorityUpdating}
            />
          </TaskRowItem>
        );
      }),
    },
    {
      key: "people",
      title: t`People`,
      isVisible: true,
      width: 120,
      order: 13,
      Component: observer(function TaskPeopleColumn({ task, gridItemProps }) {
        return (
          <TaskRowItem {...gridItemProps}>
            <TaskAvatars positions={task.positions} />
          </TaskRowItem>
        );
      }),
    },
    // {
    //   key: "comments",
    //   title: t`Activity`,
    //   isVisible: true,
    //   width: 55,
    //   order: 14,
    //   Component: ({ task, gridItemProps }) => (
    //     <TaskRowItem {...gridItemProps}>
    //       {isInternalUser ? (
    //         <TaskCommentsCount
    //           unreadCommentsCount={task.unreadCommentsCount}
    //           commentsCount={task.commentCount}
    //         />
    //       ) : (
    //         <TaskCommentsCount commentsCount={task.scopedCommentCount} />
    //       )}
    //     </TaskRowItem>
    //   ),
    // },
  ];
}
